import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { medicationApi } from 'src/api/medication';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import { CustomAvatar } from 'src/components/custom-avatar';
import MedicationStatusBadge from 'src/components/medication-status-badge/MedicationStatusBadge';
import { translateKey } from 'src/locales/translate';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import {
  GetProfileMedicationsDtoStatusEnum,
  MedicationEventDto,
  MedicationEventDtoTypeEnum,
} from 'woofwoof-api';
import { SPAN_FULL } from './medicationFormLayout';

/**
 * A medication with one entry would otherwise collapse the dialog to a strip,
 * and switching tabs would resize it under the reader's cursor.
 */
const TIMELINE_MIN_HEIGHT = 260;

interface MedicationHistoryTimelineProps {
  medicationId?: string;
  profileId?: string;
}

const EVENT_LABEL: Record<MedicationEventDtoTypeEnum, string> = {
  CREATED: 'medication.history.created',
  EDITED: 'medication.history.edited',
  DOSAGE_CHANGED: 'medication.history.dosageChanged',
  PAUSED: 'medication.history.paused',
  RESUMED: 'medication.history.resumed',
  STOPPED: 'medication.history.stopped',
  STARTED: 'medication.history.started',
};

/** The status a lifecycle entry moved the medication to, if it was one. */
function statusOf(
  event: MedicationEventDto,
): GetProfileMedicationsDtoStatusEnum | undefined {
  const change = (event.changes as
    | Record<string, { from: unknown; to: unknown }>
    | undefined)?.status;
  return change?.to as GetProfileMedicationsDtoStatusEnum | undefined;
}

/**
 * What has happened to this medication, newest first, laid out like the visit
 * course: a rail down the left with an entry beside each marker.
 */
export default function MedicationHistoryTimeline({
  medicationId,
  profileId,
}: MedicationHistoryTimelineProps) {
  const theme = useTheme();
  const { isProfessionalRoleSelected, isOrganizationAdminRoleSelected } =
    useOrganizationContext();
  const asStaff = isProfessionalRoleSelected || isOrganizationAdminRoleSelected;

  const { data, isLoading } = useQuery(
    ['medication-history', profileId, medicationId, asStaff],
    () =>
      asStaff
        ? medicationApi.medicationControllerGetProfileMedicationHistoryInOrganization(
            { profileId: profileId || '', medicationId: medicationId || '' },
          )
        : medicationApi.medicationControllerGetProfileMedicationHistory({
            profileId: profileId || '',
            medicationId: medicationId || '',
          }),
    { enabled: Boolean(profileId && medicationId) },
  );

  const events = data?.data.events ?? [];

  if (isLoading) {
    return (
      <Stack
        sx={{ ...SPAN_FULL, gap: 1.5, minHeight: TIMELINE_MIN_HEIGHT }}
      >
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
      </Stack>
    );
  }

  if (events.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ ...SPAN_FULL, minHeight: TIMELINE_MIN_HEIGHT }}
      >
        {translateKey('medication.history.empty')}
      </Typography>
    );
  }

  return (
    <Stack
      sx={{ ...SPAN_FULL, minHeight: TIMELINE_MIN_HEIGHT }}
      data-testid="medication-history"
    >
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        const status = statusOf(event);
        return (
          <Stack
            key={`${event.type}-${event.at}-${index}`}
            sx={{ flexDirection: 'row', minWidth: 0 }}
          >
            {/* The rail: a line through the marker, open at each end of the
                list so it reads as a run rather than a fence. */}
            <Stack
              sx={{
                alignItems: 'center',
                width: 28,
                flexShrink: 0,
                position: 'relative',
                justifyContent: 'center',
              }}
            >
              {index > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: 2,
                    top: 0,
                    bottom: '50%',
                    backgroundColor: theme.palette.divider,
                  }}
                />
              )}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: 2,
                    top: '50%',
                    bottom: 0,
                    backgroundColor: theme.palette.divider,
                  }}
                />
              )}
              <VetsiIcon
                width={16}
                height={16}
                sx={{ flexShrink: 0, zIndex: 1 }}
              />
            </Stack>

            <Stack
              sx={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                ml: 1,
                minWidth: 0,
              }}
            >
              <CustomAvatar
                src={event.authorAvatar}
                name={event.authorName}
                alt={event.authorName}
                sx={{ width: 36, height: 36, flexShrink: 0 }}
              />

              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {event.authorName || translateKey('medication.history.system')}
                </Typography>
                {event.authorOrganizationName && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {event.authorOrganizationName}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled">
                  {format(new Date(event.at), 'd MMMM y, HH:mm', { locale: pl })}
                </Typography>
              </Stack>

              {/* A lifecycle move reads as the status it produced; everything
                  else says what was done. */}
              {status ? (
                <MedicationStatusBadge status={status} />
              ) : (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {translateKey(EVENT_LABEL[event.type] ?? '')}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
